/**
 * GraphQL queries and hooks — technologyCapability pillar.
 * Split from graphql/queries.ts (issue #1409).
 */

import { gql } from '@apollo/client';
import { useQuery, type QueryResult } from '@apollo/client/react';
import type {
  TechnologyCapability,
  TechnologyCapabilityConnection,
  TechnologyCapabilityFilter,
} from '../types/technologyCapability';

/**
 * Fragment for TechnologyCapability fields
 */
export const TECHNOLOGY_CAPABILITY_FIELDS = gql`
  fragment TechnologyCapabilityFields on TechnologyCapability {
    entityId
    entityType
    name
    description
    domainId
    tags
    notes
    _createdAt
    _createdBy
    _updatedAt
    _updatedBy
    _deletedAt
  }
`;

/**
 * Query to get a single technology capability by ID
 */
export const GET_TECHNOLOGY_CAPABILITY = gql`
  ${TECHNOLOGY_CAPABILITY_FIELDS}
  query GetTechnologyCapability($id: ID!) {
    technologyCapability(id: $id) {
      ...TechnologyCapabilityFields
    }
  }
`;

/**
 * Query to list all technology capabilities with filtering and pagination
 */
export const LIST_TECHNOLOGY_CAPABILITIES = gql`
  ${TECHNOLOGY_CAPABILITY_FIELDS}
  query ListTechnologyCapabilities(
    $filter: TechnologyCapabilityFilter
    $first: Int
    $after: String
  ) {
    technologyCapabilities(filter: $filter, first: $first, after: $after) {
      edges {
        node {
          ...TechnologyCapabilityFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

/**
 * Hook to get a single technology capability
 */
export function useTechnologyCapability(
  id: string
): QueryResult<{ technologyCapability: TechnologyCapability | null }> {
  return useQuery(GET_TECHNOLOGY_CAPABILITY, {
    variables: { id },
    skip: !id,
  });
}

/**
 * Hook to list technology capabilities with optional filtering
 */
export function useTechnologyCapabilities(
  filter?: TechnologyCapabilityFilter,
  first: number = 50
): QueryResult<{ technologyCapabilities: TechnologyCapabilityConnection }> {
  return useQuery(LIST_TECHNOLOGY_CAPABILITIES, {
    variables: { filter, first },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}
